import httpClient from '../httpClient';
import {
  BasicUserInfo,
  IdentityUserInfo,
  User,
  UserPreferences,
} from 'models/User';
import { UploadedFileResponse } from 'models/File';
import { AxiosRequestConfig } from 'axios';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';

const usersPath = (routePath: TemplateStringsArray) => `users/${routePath}`;

function getCurrentUser() {
  return httpClient.get<Record<'user', User>>(usersPath`auth-user`);
}

function getSearchByEmails(emails: string[]) {
  const params = new URLSearchParams({ emails: emails.join(',') });

  return httpClient.get<IdentityUserInfo[]>(usersPath`search-by-emails`, {
    params,
  });
}

function getUserBasicInfo(email: string) {
  return httpClient.get<Record<'user', BasicUserInfo>>(usersPath`basic-info`, {
    params: { email },
  });
}

function patchUser(id: string, user: Partial<User>) {
  return httpClient.patch<Record<'user', User>>(`users/${id}`, user);
}

function deleteAccount() {
  return httpClient.delete<Record<'message', string>>(
    'users/delete-account-request',
  );
}

function deleteAccountRevert() {
  return httpClient.post<Record<'message', string>>(
    'users/delete-account-revert',
  );
}

function changePassword(oldPassword: string, newPassword: string) {
  return httpClient.post(usersPath`change-password`, {
    oldPassword,
    newPassword,
  });
}

async function uploadFile(
  userId: string,
  file: File,
  fileName: string,
  config?: AxiosRequestConfig,
) {
  const fileParams = getFileParams(fileName, file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `users/${userId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

function deleteFile(userId: string, fileName: string, fileId: string) {
  return httpClient.delete<{ fileId: string }>(
    `users/${userId}/files/${fileName}/${fileId}`,
  );
}

function phoneVerification(contactNumber: string) {
  return httpClient.post('users/initiate-phone-verification', {
    contactNumber,
  });
}

function checkPhoneVerification(verificationCode: string) {
  return httpClient.post<User>('users/check-phone-verification-code', {
    verificationCode,
  });
}

function subscribeToNewsletter() {
  return httpClient.post(`users/subscribe-newsletter`);
}

function unsubscribeFromNewsletter() {
  return httpClient.post(`users/unsubscribe-newsletter`);
}

function getUser(id: string) {
  return httpClient.get<Record<'user', User>>(`users/${id}`);
}

function remindMeLaterToVerifyPhone() {
  return httpClient.post('users/verify-phone-number-remind-later');
}

function updatePreferences(preferences: Partial<UserPreferences>) {
  return httpClient.patch<User>('users/preferences', preferences);
}

export default {
  getCurrentUser,
  getUserBasicInfo,
  patchUser,
  changePassword,
  uploadFile,
  deleteFile,
  deleteAccount,
  deleteAccountRevert,
  checkPhoneVerification,
  phoneVerification,
  subscribeToNewsletter,
  unsubscribeFromNewsletter,
  getUser,
  getSearchByEmails,
  remindMeLaterToVerifyPhone,
  updatePreferences,
};
